import { useEffect, useRef, useState } from 'react';
import { useNotifications } from './useNotifications';
const SEVERITIES = {
    info: 'info',
    success: 'success',
    warning: 'warning',
    error: 'error',
};
/** Standard-Mapping eines BusEvent auf eine Benachrichtigung. */
export function defaultMapBusEvent(event) {
    const title = event.title || event.type;
    if (!title)
        return null;
    const severity = SEVERITIES[String(event.severity)] ?? 'info';
    return {
        id: event.id,
        title,
        body: event.body,
        severity,
        timestamp: event.timestamp,
        source: event.source,
        entity: event.entity ?? event.target,
    };
}
const MAX_BACKOFF_MS = 60000;
/** `<pfad von url>/ticket` (Query/Fragment von `url` entfallen). */
function defaultTicketUrl(url) {
    const path = url.split(/[?#]/, 1)[0] ?? url;
    return `${path.replace(/\/+$/, '')}/ticket`;
}
function withParam(url, name, value) {
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}${name}=${encodeURIComponent(value)}`;
}
/** Ergebnis des Ticket-Abrufs: Ticket, oder `null` = Gateway ohne Endpoint (404/405). */
async function fetchTicket(ticketUrl, headers, signal) {
    const res = await fetch(ticketUrl, {
        method: 'POST',
        headers,
        credentials: 'omit',
        cache: 'no-store',
        signal,
    });
    if (res.status === 404 || res.status === 405)
        return null;
    if (!res.ok)
        throw new Error(`sse ticket: HTTP ${res.status}`);
    const body = (await res.json());
    if (!body || typeof body.ticket !== 'string' || !body.ticket)
        throw new Error('sse ticket: no ticket');
    return body.ticket;
}
/**
 * Optionaler Adapter: abonniert den bus-gateway-SSE-Stream und speist eingehende
 * Events in den NotificationProvider.
 *
 * Token-Modus: vor JEDEM Verbindungsaufbau (auch jedem Reconnect) wird per
 * `POST <url>/ticket` ein frisches Einmal-Ticket geholt und `<url>?ticket=…`
 * geoeffnet. Bei einem Fehler wird der EventSource geschlossen (dessen
 * Auto-Reconnect wuerde das verbrauchte Ticket wiederverwenden) und mit
 * exponentiellem Backoff (1 s .. 60 s) und neuem Ticket neu verbunden. Nur bei
 * 404/405 (Gateway ohne Ticket-Endpoint) Rueckfall auf `?access_token=`.
 *
 * No-op, wenn `enabled=false`, kein `url`, weder `token` noch `withCredentials`
 * (Cookie-Modus), oder EventSource/fetch fehlt (SSR). Im Cookie-Modus wird ohne
 * Token und ohne Ticket verbunden und der EventSource mit
 * `{ withCredentials: true }` erzeugt.
 */
export function useBusNotifications({ url, token, ticketUrl, authHeaders, withCredentials = false, enabled = true, map = defaultMapBusEvent, }) {
    const { add } = useNotifications();
    const [connected, setConnected] = useState(false);
    const mapRef = useRef(map);
    mapRef.current = map;
    const addRef = useRef(add);
    addRef.current = add;
    const authHeadersRef = useRef(authHeaders);
    authHeadersRef.current = authHeaders;
    useEffect(() => {
        if (!enabled || !url || (!token && !withCredentials) || typeof EventSource === 'undefined') {
            return;
        }
        if (token && typeof fetch !== 'function')
            return;
        let source = null;
        let backoff = 1000;
        let reconnectTimer = null;
        let closed = false;
        const abort = typeof AbortController !== 'undefined' ? new AbortController() : null;
        const scheduleReconnect = () => {
            if (closed || reconnectTimer)
                return;
            const delay = Math.min(backoff, MAX_BACKOFF_MS);
            reconnectTimer = setTimeout(() => {
                reconnectTimer = null;
                backoff = Math.min(backoff * 2, MAX_BACKOFF_MS);
                void connect();
            }, delay);
        };
        const open = (streamUrl, init) => {
            try {
                source = init ? new EventSource(streamUrl, init) : new EventSource(streamUrl);
            }
            catch {
                source = null;
                scheduleReconnect();
                return;
            }
            source.onopen = () => {
                backoff = 1000;
                setConnected(true);
            };
            source.onmessage = (ev) => {
                let data;
                try {
                    data = JSON.parse(ev.data);
                }
                catch {
                    return;
                }
                const input = mapRef.current(data);
                if (input)
                    addRef.current(input);
            };
            source.onerror = () => {
                setConnected(false);
                // Schliessen statt Auto-Reconnect: das Ticket ist verbraucht.
                if (source) {
                    source.close();
                    source = null;
                }
                scheduleReconnect();
            };
        };
        const connect = async () => {
            if (closed)
                return;
            if (!token) {
                // Cookie-/Same-Origin-Modus: ohne Token verbinden, Credentials mitsenden.
                open(url, { withCredentials: true });
                return;
            }
            const target = ticketUrl || defaultTicketUrl(url);
            let ticket;
            try {
                const custom = authHeadersRef.current;
                const headers = custom
                    ? await custom({ method: 'POST', url: target })
                    : { Authorization: `Bearer ${token}` };
                if (closed)
                    return;
                ticket = await fetchTicket(target, headers, abort?.signal);
            }
            catch {
                if (!closed)
                    scheduleReconnect();
                return;
            }
            if (closed)
                return;
            // Rueckfall nur fuer Gateways ohne /sse/ticket (404/405).
            open(ticket ? withParam(url, 'ticket', ticket) : withParam(url, 'access_token', token));
        };
        void connect();
        return () => {
            closed = true;
            abort?.abort();
            if (reconnectTimer)
                clearTimeout(reconnectTimer);
            if (source)
                source.close();
            setConnected(false);
        };
    }, [url, token, ticketUrl, withCredentials, enabled]);
    return { connected };
}
